// src/services/wishlistService.js - UPDATED WITH VALIDATION
import { 
  collection, doc, getDoc, getDocs, setDoc, deleteDoc, 
  query, where, Timestamp, addDoc, updateDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';

class WishlistService {
  async getWishlist(bookId, userId) {
    try {
      const wishlistRef = collection(db, `pb-books/${bookId}/wishlists/${userId}/items`);
      const snapshot = await getDocs(wishlistRef);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        courseId: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting wishlist:', error);
      throw error;
    }
  }

  async addToWishlist(bookId, userId, courseId) {
    try {
      if (!userId) throw new Error('User must be authenticated');
      if (!courseId) throw new Error('Course ID is required');

      const itemRef = doc(db, `pb-books/${bookId}/wishlists/${userId}/items/${courseId}`);
      
      // Check if already exists
      const existingItem = await getDoc(itemRef);
      if (existingItem.exists()) {
        return { courseId, userId, alreadyExists: true };
      }

      await setDoc(itemRef, {
        courseId,
        userId,
        addedAt: Timestamp.now()
      });

      return { courseId, userId };
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      throw error;
    }
  }

  async removeFromWishlist(bookId, userId, courseId) {
    try {
      if (!userId) throw new Error('User must be authenticated');
      
      const itemRef = doc(db, `pb-books/${bookId}/wishlists/${userId}/items/${courseId}`);
      await deleteDoc(itemRef);
      
      return { success: true };
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      throw error;
    }
  }

  async submitWishlistQuery(bookId, userId, wishlist, query) {
    try {
      if (!userId) throw new Error('User must be authenticated');
      if (!query?.trim()) throw new Error('Query message is required');
      if (query.length > 2000) throw new Error('Query message too long (max 2000 characters)');
      if (!wishlist || wishlist.length === 0) throw new Error('Wishlist is empty');
      if (wishlist.length > 20) throw new Error('Too many courses in wishlist (max 20)');

      const queriesRef = collection(db, `pb-books/${bookId}/wishlist-queries`);
      
      const queryData = {
        userId,
        courseIds: wishlist.map(item => item.courseId),
        query: query.trim(),
        status: 'pending',
        createdAt: Timestamp.now()
      };

      const docRef = await addDoc(queriesRef, queryData);

      return { 
        success: true, 
        queryId: docRef.id 
      };
    } catch (error) {
      console.error('Error submitting wishlist query:', error);
      throw error;
    }
  }

  async getUserQueries(bookId, userId) {
    try {
      const queriesRef = collection(db, `pb-books/${bookId}/wishlist-queries`);
      const q = query(queriesRef, where('userId', '==', userId));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting user queries:', error);
      throw error;
    }
  }

  async getAllQueries(bookId) {
    try {
      const queriesRef = collection(db, `pb-books/${bookId}/wishlist-queries`);
      const snapshot = await getDocs(queriesRef);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting all queries:', error);
      throw error;
    }
  }

  async updateQueryStatus(bookId, queryId, status, response = null) {
    try {
      if (!['pending', 'in-progress', 'responded', 'closed'].includes(status)) {
        throw new Error('Invalid status');
      }

      const queryRef = doc(db, `pb-books/${bookId}/wishlist-queries/${queryId}`);
      
      const updateData = {
        status,
        updatedAt: Timestamp.now()
      };

      if (response) {
        updateData.response = response;
        updateData.respondedAt = Timestamp.now();
      }

      await updateDoc(queryRef, updateData);

      return { success: true };
    } catch (error) {
      console.error('Error updating query status:', error);
      throw error;
    }
  }

  async getWishlistStats(bookId) {
    try {
      // Get all wishlist items across all users
      const wishlistsRef = collection(db, `pb-books/${bookId}/wishlists`);
      const wishlistsSnapshot = await getDocs(wishlistsRef);
      
      const courseWishlistCount = {};
      let totalItems = 0;

      for (const userDoc of wishlistsSnapshot.docs) {
        const itemsRef = collection(db, `pb-books/${bookId}/wishlists/${userDoc.id}/items`);
        const itemsSnapshot = await getDocs(itemsRef);
        
        itemsSnapshot.docs.forEach(itemDoc => {
          const courseId = itemDoc.data().courseId;
          courseWishlistCount[courseId] = (courseWishlistCount[courseId] || 0) + 1;
          totalItems++;
        });
      }

      return {
        totalItems,
        uniqueUsers: wishlistsSnapshot.docs.length,
        courseWishlistCount,
        topCourses: Object.entries(courseWishlistCount)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10)
          .map(([courseId, count]) => ({ courseId, count }))
      };
    } catch (error) {
      console.error('Error getting wishlist stats:', error);
      throw error;
    }
  }
}

export const wishlistService = new WishlistService();